import {
    Accordion,
    Badge,
    Box,
    Flex,
    Heading,
    SimpleGrid,
    Stack,
    Text,
} from '@chakra-ui/react';
import { LuChevronDown } from 'react-icons/lu';

const clampScore = (value) => {
    const numeric = typeof value === 'number' ? value : parseFloat(value);
    if (Number.isNaN(numeric)) return 0;
    return Math.max(0, Math.min(10, numeric));
};

const scoreDisplay = (value) => clampScore(value).toFixed(1);

const ratingVisuals = (value) => {
    const score = clampScore(value);
    if (score >= 7) {
        return { text: 'green.600', bg: 'green.50', border: 'green.200', label: 'High Conviction' };
    }
    if (score >= 4) {
        return { text: 'yellow.600', bg: 'yellow.50', border: 'yellow.200', label: 'Worth Watching' };
    }
    return { text: 'red.600', bg: 'red.50', border: 'red.200', label: 'High Risk' };
};

const badgeTone = (text) => {
    if (!text) return 'gray';
    const lowered = text.toLowerCase();
    if (lowered.includes('wide') || lowered.includes('strong') || lowered.includes('excellent')) return 'green';
    if (lowered.includes('moderate') || lowered.includes('narrow') || lowered.includes('balanced')) return 'yellow';
    if (lowered.includes('weak') || lowered.includes('poor') || lowered.includes('none')) return 'red';
    return 'blue';
};

const extractBadgeLabel = (text, fallback) => {
    if (!text) return fallback;
    const lowered = text.toLowerCase();
    if (lowered.includes('wide')) return 'Wide Moat';
    if (lowered.includes('strong')) return 'Strong Moat';
    if (lowered.includes('moderate')) return 'Moderate Moat';
    if (lowered.includes('narrow')) return 'Narrow Moat';
    if (lowered.includes('weak')) return 'Weak Moat';
    if (lowered.includes('none')) return 'No Moat';
    if (lowered.includes('excellent')) return 'Excellent';
    if (lowered.includes('good')) return 'Good';
    if (lowered.includes('average')) return 'Average';
    if (lowered.includes('poor')) return 'Poor';
    return text.length > 48 ? `${text.slice(0, 45)}...` : text;
};

const SectionText = ({ text }) => (
    <Text whiteSpace="pre-wrap" wordBreak="break-word" color="gray.700">
        {text || 'No insights provided.'}
    </Text>
);

function BuffettRatingCard({ rating }) {
    if (!rating) return null;
    const sections = [
        { value: 'bull', title: 'Bull Case', content: rating?.bull_case },
        { value: 'bear', title: 'Bear Case', content: rating?.bear_case },
        { value: 'risks', title: 'Key Risks', content: rating?.key_risks },
    ];
    const visuals = ratingVisuals(rating?.rating);

    return (
        <Box borderWidth="1px" borderRadius="xl" p={6} bg="white" boxShadow="sm" w="100%" maxW="100%" overflow="hidden">
            <Stack gap={6}>
                <Flex align={{ base: 'flex-start', md: 'center' }} justify="space-between" flexWrap="wrap" gap={4}>
                    <Box>
                        <Text fontSize="sm" textTransform="uppercase" color="gray.500" letterSpacing="wider">
                            Buffett Rating
                        </Text>
                        <Heading size="lg">{rating.symbol}</Heading>
                        <Text mt={1} color="gray.600">
                            {visuals.label}
                        </Text>
                    </Box>
                    <Box
                        textAlign="center"
                        bg={visuals.bg}
                        borderWidth="2px"
                        borderColor={visuals.border}
                        px={8}
                        py={4}
                        borderRadius="2xl"
                        minW="180px"
                    >
                        <Text fontSize="5xl" fontWeight="bold" color={visuals.text} lineHeight="1">
                            {scoreDisplay(rating?.rating)}
                        </Text>
                        <Text fontSize="sm" fontWeight="semibold" color={visuals.text} letterSpacing="wide">
                            out of 10
                        </Text>
                    </Box>
                </Flex>

                <Box bg="gray.50" p={4} borderRadius="md">
                    <Text fontSize="lg" fontWeight="semibold">
                        Thesis
                    </Text>
                    <SectionText text={rating?.thesis} />
                </Box>

                <Accordion.Root multiple borderWidth="1px" borderColor="gray.200" borderRadius="md">
                    {sections.map((section) => (
                        <Accordion.Item key={section.value} value={section.value}>
                            <Accordion.ItemTrigger cursor="pointer" p={4}>
                                <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                                    {section.title}
                                </Box>
                                <Accordion.ItemIndicator>
                                    <LuChevronDown />
                                </Accordion.ItemIndicator>
                            </Accordion.ItemTrigger>
                            <Accordion.ItemContent>
                                <Box pb={4} px={4}>
                                    <SectionText text={section.content} />
                                </Box>
                            </Accordion.ItemContent>
                        </Accordion.Item>
                    ))}
                </Accordion.Root>

                <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                    <Box borderWidth="1px" borderRadius="lg" p={4} overflow="hidden">
                        <Flex align="center" justify="space-between" mb={2}>
                            <Text fontSize="sm" textTransform="uppercase" color="gray.500">
                                Moat Analysis
                            </Text>
                            <Badge colorPalette={badgeTone(rating?.moat_analysis)} variant="solid">
                                {extractBadgeLabel(rating?.moat_analysis, 'Unknown')}
                            </Badge>
                        </Flex>
                        <SectionText text={rating?.moat_analysis} />
                    </Box>
                    <Box borderWidth="1px" borderRadius="lg" p={4} overflow="hidden">
                        <Flex align="center" justify="space-between" mb={2}>
                            <Text fontSize="sm" textTransform="uppercase" color="gray.500">
                                Management Quality
                            </Text>
                            <Badge colorPalette={badgeTone(rating?.management_quality)} variant="solid">
                                {extractBadgeLabel(rating?.management_quality, 'Unknown')}
                            </Badge>
                        </Flex>
                        <SectionText text={rating?.management_quality} />
                    </Box>
                </SimpleGrid>

                <Box borderWidth="1px" borderRadius="lg" p={4}>
                    <Text fontSize="sm" textTransform="uppercase" color="gray.500" mb={2}>
                        Valuation Notes
                    </Text>
                    <SectionText text={rating?.valuation_notes} />
                </Box>
            </Stack>
        </Box>
    );
}

export default BuffettRatingCard;
